import { useSyncExternalStore } from 'react';
import type { Point } from './api.types';

export type Candidate = {
  id: string;
  label: string;
  address: string | null;
  point: Point;
  savedAt: string;
};

const KEY = 'ienone:candidates';
const EVENT = 'ienone:candidates-changed';
const MAX_CANDIDATES = 5;

function read(): Candidate[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Candidate[];
  } catch {
    return [];
  }
}

function write(list: Candidate[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

// Caching the snapshot is required so React doesn't see a new reference on every read.
let cached: Candidate[] = read();
let cachedJson = JSON.stringify(cached);
function getSnapshot(): Candidate[] {
  if (typeof window === 'undefined') return cached;
  const fresh = read();
  const freshJson = JSON.stringify(fresh);
  if (freshJson !== cachedJson) {
    cached = fresh;
    cachedJson = freshJson;
  }
  return cached;
}

function getServerSnapshot(): Candidate[] {
  return [];
}

export function useCandidates(): Candidate[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export const candidateStore = {
  list(): Candidate[] {
    return read();
  },

  add(candidate: Omit<Candidate, 'id' | 'savedAt'>): Candidate[] {
    const id = `${candidate.point.lat.toFixed(5)}_${candidate.point.lon.toFixed(5)}`;
    const next: Candidate = {
      ...candidate,
      id,
      savedAt: new Date().toISOString(),
    };
    const existing = read().filter((c) => c.id !== id);
    const merged = [next, ...existing].slice(0, MAX_CANDIDATES);
    write(merged);
    return merged;
  },

  remove(id: string): Candidate[] {
    const next = read().filter((c) => c.id !== id);
    write(next);
    return next;
  },

  clear(): void {
    write([]);
  },
};
